import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import {
  Building2,
  Code2,
  CreditCard,
  Heart,
  Home,
  Kanban,
  LogOut,
  Menu,
  MessageSquare,
  Phone,
  PhoneCall,
  Plug,
  Settings,
  Shield,
  Users,
  X,
  Zap
} from 'lucide-react'
import { apiService } from '../../services/api'

interface NavigationItem {
  name: string
  href: string
  icon: ComponentType<{ className?: string }>
  highlight?: boolean
  adminOnly?: boolean
}

interface AuthUser {
  name?: string
  email?: string
  is_platform_admin?: boolean
  membership?: {
    role?: string
  }
  organization?: {
    name?: string
  }
}

interface SyncIssue {
  severity?: 'error' | 'warning' | 'info'
}

interface TelephonySyncResponse {
  issues?: SyncIssue[]
  configuration?: {
    wavoip?: {
      instance_name?: string
      phone_number?: string
      device?: {
        status?: string
      }
    }
  }
}

const navigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/app', icon: Home },
  { name: 'Ligações', href: '/app/calls', icon: PhoneCall, highlight: true },
  { name: 'Pipeline', href: '/app/pipeline', icon: Kanban, highlight: true },
  { name: 'Agentes', href: '/app/agents', icon: Users },
  { name: 'Números', href: '/app/phone-numbers', icon: Phone },
  { name: 'Conversas', href: '/app/conversations', icon: MessageSquare },
  { name: 'Integrações', href: '/app/developer', icon: Code2 },
  { name: 'Configurações', href: '/app/settings', icon: Settings }
]

const adminNavigation: NavigationItem[] = [
  { name: 'Admin', href: '/app/admin', icon: Shield },
  { name: 'Organizações', href: '/app/admin?tab=organizations', icon: Building2 },
  { name: 'Usuários', href: '/app/admin?tab=users', icon: Users },
  { name: 'Conexões', href: '/app/admin?tab=connections', icon: Plug },
  { name: 'Cobrança', href: '/app/admin?tab=billing', icon: CreditCard },
  { name: 'Plataforma', href: '/app/admin?tab=platform', icon: Settings }
]

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary-600 text-white shadow-sm">
        <Zap className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <span className="block text-lg font-bold leading-tight text-slate-950">AICall</span>
        <span className="block truncate text-xs font-medium text-slate-500">Operação de voz IA</span>
      </div>
    </div>
  )
}

function SidebarContent({
  items,
  adminItems,
  user,
  telephony,
  loadingStatus,
  onNavigate,
  onLogout
}: {
  items: NavigationItem[]
  adminItems?: NavigationItem[]
  user: AuthUser | null
  telephony: TelephonySyncResponse | null
  loadingStatus: boolean
  onNavigate?: () => void
  onLogout: () => void
}) {
  const telephonyState = useMemo(() => {
    const issues = telephony?.issues || []
    const hasError = issues.some((issue) => issue.severity === 'error')
    const hasWarning = issues.some((issue) => issue.severity === 'warning')
    const instanceName = telephony?.configuration?.wavoip?.instance_name
    const phoneNumber = telephony?.configuration?.wavoip?.phone_number
    const deviceStatus = telephony?.configuration?.wavoip?.device?.status

    if (loadingStatus) {
      return {
        dotClass: 'bg-warning-500 animate-pulse',
        title: 'Validando telefonia',
        description: 'Consultando sincronismo de números, SIP e provedores.'
      }
    }

    if (hasError) {
      return {
        dotClass: 'bg-danger-500',
        title: 'Telefonia requer atenção',
        description: 'Há falhas na sincronização. Abra Números ou Configurações para revisar o diagnóstico.'
      }
    }

    if (hasWarning) {
      return {
        dotClass: 'bg-warning-500',
        title: 'Telefonia com alerta',
        description: 'A operação está disponível, mas há pontos pendentes para revisão.'
      }
    }

    if (instanceName || phoneNumber || deviceStatus) {
      return {
        dotClass: 'bg-success-500',
        title: 'Telefonia pronta',
        description: [instanceName, phoneNumber, deviceStatus].filter(Boolean).join(' • ')
      }
    }

    return {
      dotClass: 'bg-slate-400',
      title: 'Telefonia sem dados',
      description: 'Ainda não foi possível carregar o status operacional.'
    }
  }, [loadingStatus, telephony])

  const userRoleLabel = formatRoleLabel(user?.membership?.role)
  const userName = user?.name || 'Usuário'
  const organizationName = user?.organization?.name || 'Workspace'

  return (
    <>
      <div className="flex h-20 items-center border-b border-slate-200 px-4">
        <Brand />
      </div>

      <div className="border-b border-slate-200 px-4 py-3">
        <p className="truncate text-sm font-semibold text-slate-950">{userName}</p>
        <p className="truncate text-xs text-slate-500">{organizationName}</p>
        <span className="badge mt-1 bg-slate-100 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
          {userRoleLabel}
        </span>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4 scrollbar-thin" aria-label="Navegação principal">
        {items.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.href === '/app'}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition',
                isActive
                  ? 'bg-slate-950 text-white shadow-sm'
                  : item.highlight
                    ? 'text-primary-700 hover:bg-primary-50 hover:text-primary-800'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
              ].join(' ')
            }
          >
            {({ isActive }) => (
              <>
                <item.icon className={`h-5 w-5 flex-none ${isActive ? 'text-white' : 'text-current'}`} />
                <span className="truncate">{item.name}</span>
              </>
            )}
          </NavLink>
        ))}
        {adminItems && adminItems.length > 0 && (
          <>
            <div className="my-3 border-t border-slate-200" />
            <p className="px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Administração</p>
            {adminItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/app'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  [
                    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition',
                    isActive
                      ? 'bg-slate-950 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={`h-5 w-5 flex-none ${isActive ? 'text-white' : 'text-current'}`} />
                    <span className="truncate">{item.name}</span>
                  </>
                )}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-slate-200 p-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 flex-none rounded-full ${telephonyState.dotClass}`} />
            <span className="text-xs font-semibold text-slate-700 truncate">{telephonyState.title}</span>
          </div>
          <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-slate-500">{telephonyState.description}</p>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="btn-secondary mt-3 w-full"
        >
          <LogOut className="h-4 w-4" />
          Sair
        </button>
      </div>
    </>
  )
}

function formatRoleLabel(role?: string) {
  switch (role) {
    case 'owner':
      return 'Owner'
    case 'admin':
      return 'Admin'
    case 'billing':
      return 'Financeiro'
    case 'operator':
      return 'Operador'
    case 'viewer':
      return 'Leitura'
    default:
      return 'Usuário'
  }
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [telephony, setTelephony] = useState<TelephonySyncResponse | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    let active = true

    const loadShellData = async () => {
      try {
        setLoadingStatus(true)
        const [meResponse, telephonyResponse] = await Promise.allSettled([
          apiService.getMe(),
          apiService.getTelephonySync()
        ])

        if (!active) return

        if (meResponse.status === 'fulfilled') {
          setUser(meResponse.value.data?.data?.user || null)
        }

        if (telephonyResponse.status === 'fulfilled') {
          setTelephony(telephonyResponse.value.data?.data || null)
        } else {
          setTelephony(null)
        }
      } finally {
        if (active) {
          setLoadingStatus(false)
        }
      }
    }

    void loadShellData()
    return () => {
      active = false
    }
  }, [])

  const visibleNavigation = useMemo(() => {
    return navigation
  }, [])

  const visibleAdminNavigation = useMemo(() => {
    if (user?.is_platform_admin) return adminNavigation
    const role = user?.membership?.role
    const isAdmin = role === 'owner' || role === 'admin'
    if (isAdmin) return adminNavigation.filter((item) => item.name === 'Admin')
    return []
  }, [user?.is_platform_admin, user?.membership?.role])

  function handleLogout() {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    setSidebarOpen(false)
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar overlay */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <button
          type="button"
          aria-label="Fechar menu"
          className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        <aside className="fixed inset-y-0 left-0 flex w-[18rem] max-w-[86vw] flex-col border-r border-slate-200 bg-white shadow-xl">
          <div className="absolute right-3 top-3">
            <button
              type="button"
              aria-label="Fechar navegação"
              onClick={() => setSidebarOpen(false)}
              className="btn-icon"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <SidebarContent
            items={visibleNavigation}
            adminItems={visibleAdminNavigation}
            user={user}
            telephony={telephony}
            loadingStatus={loadingStatus}
            onNavigate={() => setSidebarOpen(false)}
            onLogout={handleLogout}
          />
        </aside>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col lg:border-r lg:border-slate-200 lg:bg-white">
        <SidebarContent
          items={visibleNavigation}
          adminItems={visibleAdminNavigation}
          user={user}
          telephony={telephony}
          loadingStatus={loadingStatus}
          onLogout={handleLogout}
        />
      </aside>

      <div className="lg:pl-72">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 shadow-sm backdrop-blur-md lg:hidden">
          <button
            type="button"
            aria-label="Abrir navegação"
            onClick={() => setSidebarOpen(true)}
            className="btn-icon"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Brand />
        </header>

        <main className="py-5 sm:py-6">
          <div className="mx-auto w-full max-w-[1500px] px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
          <footer className="mt-16 border-t border-slate-100 pb-8 pt-6 text-center">
            <a
              href="https://greenhub.buzz"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-emerald-500 transition-colors"
            >
              Feito com <Heart className="h-3.5 w-3.5 text-red-400" /> por <span className="font-semibold text-slate-500 hover:text-emerald-500">GreenHub.buzz</span>
            </a>
          </footer>
        </main>
      </div>
    </div>
  )
}
